// observability.ts — SCALE-2 · hardened by HOTFIX-1
//
// HOTFIX-1 — THIS MODULE MUST NEVER BE ABLE TO STOP THE APP BOOTING.
//
// It shipped with `import * as Sentry from '@sentry/react'` at the top.
// `src/lib/debug.ts` imports this file, and essentially every module in
// the app imports `debug` — so that one static import put `@sentry/react`
// on the critical path of the entire bundle. The SCALE-2 pass added
// `@sentry/react` + `@sentry/vite-plugin` to package.json but could not
// run an install, so neither package is in `node_modules` or in either
// lockfile. Result: Vite cannot resolve the import, every module that
// touches `debug` fails transform, `#root` never gets a child, and the
// page sits there loading forever.
//
// So the SDK is now:
//   • loaded with a LAZY dynamic import, never a static one;
//   • only loaded at all when VITE_SENTRY_DSN is set;
//   • memoised, and every load/init/capture is wrapped in try/catch;
//   • never awaited at module scope — nothing here blocks first paint.
// A missing package, a missing DSN, or an SDK that throws all degrade
// to a console line. See `vite.config.ts` for the matching build-side
// guard (the plugin + a resolve stub for a missing @sentry/react).
//
// Orbit's error-tracking layer for the web app. Sentry underneath, but
// every call site goes through THIS module, never through
// `@sentry/react` directly, for three reasons:
//
//   1. **Scrubbing is not optional.** Orbit handles client emails,
//      contracts, signing tokens, gallery access codes and payment
//      data. `beforeSend` below is the single choke point where all of
//      that gets stripped, and it can only be the single choke point
//      if nothing bypasses it.
//   2. **No DSN must not mean a crash.** `VITE_SENTRY_DSN` is
//      environment-specific and will be unset on a fresh clone. Every
//      export here degrades to a console line rather than throwing.
//   3. **One place to swap vendors.** If Sentry ever gets replaced the
//      blast radius is this file plus `main.tsx`.
//
// WHAT THIS DELIBERATELY DOES NOT DO:
//   • No Session Replay. It records the DOM, and Orbit's DOM contains
//     client names, contract bodies and invoice amounts. Turning it on
//     would make this module's scrubbing meaningless.
//   • No profiling, no `sendDefaultPii`, no automatic IP capture.
//   • No console breadcrumbs above `warn` — `debug.log` payloads carry
//     row data.
//
// PRIVACY COMMITMENTS THIS HONOURS (src/pages/Privacy.tsx):
//   • §1 promises automatic collection is limited to "sign-in events,
//     device identifiers, IP address and basic browser/OS metadata for
//     security and abuse prevention" plus "anonymized product
//     analytics". Error reports are troubleshooting under §2 ("to
//     respond to support requests and troubleshoot bugs"), and they are
//     anonymised here: opaque uuids only, never a name or an email.
//   • §3 lists sub-processors. **Sentry is not on that list yet.**
//     Adding it is a founder action, written up in
//     docs/lovable-deploy-prompts.md. Until it is on the list, do not
//     turn this on in production.
//   • Orbit is a *processor* for client data (§1). Client PII must
//     never leave the database into a third-party error tracker, which
//     is what `scrubValue` below is for.

// HOTFIX-1 — NO STATIC `import ... from '@sentry/react'`. See the header.
// The SDK surface is described structurally instead, so neither the
// bundler nor `tsc` needs the package to be present for this file to
// compile. Only the six calls below are ever made against it; if the
// real SDK's shape ever drifts from this, the try/catch around every
// call site is what keeps that from being a crash.

/** The subset of `@sentry/react` this module actually uses. */
interface SentryScopeLike {
  setTag(key: string, value: string): void;
  setLevel(level: string): void;
  setContext(key: string, context: Record<string, unknown> | null): void;
}

interface SentryLike {
  init(options: Record<string, unknown>): void;
  setUser(user: { id?: string } | null): void;
  setTag(key: string, value: string): void;
  withScope(callback: (scope: SentryScopeLike) => void): void;
  captureException(error: unknown): void;
  captureMessage(message: string, level?: string): void;
}

/**
 * The event shape `beforeSend` hands us. Structural on purpose (see
 * above) — this is deliberately loose because the only thing we do with
 * an event is redact fields off it.
 */
export interface SentryEventLike {
  user?: {
    id?: unknown;
    ip_address?: unknown;
    email?: unknown;
    username?: unknown;
  } | null;
  request?: {
    url?: unknown;
    query_string?: unknown;
    cookies?: unknown;
    data?: unknown;
    headers?: Record<string, unknown>;
  };
  message?: unknown;
  exception?: { values?: Array<{ value?: unknown }> };
  extra?: Record<string, unknown>;
  contexts?: Record<string, unknown>;
  tags?: Record<string, unknown>;
}

// Injected by vite.config.ts at build time. Declared here so the rest
// of the app never has to know about the globals.
declare const __ORBIT_RELEASE__: string;
declare const __ORBIT_ENV__: string;

const DSN = (import.meta.env.VITE_SENTRY_DSN as string | undefined)?.trim();

/**
 * True when a DSN is configured — i.e. when reporting is *supposed* to
 * be on. It is NOT a promise that the SDK loaded; that's `sdkReady`.
 * Callers use this to short-circuit before paying for a dynamic import.
 */
export const observabilityEnabled = Boolean(DSN);

let initialised = false;
/** Flips true only once `Sentry.init` has actually run without throwing. */
let sdkReady = false;
let sentryPromise: Promise<SentryLike | null> | null = null;

/**
 * HOTFIX-1 — lazy, memoised, never-throwing SDK load.
 *
 * Resolves to `null` (loudly, once) when the package isn't installed or
 * the chunk fails to fetch. Callers must treat `null` as "reporting is
 * off" and carry on; nothing in the app may await this on a render path.
 */
function loadSentry(): Promise<SentryLike | null> {
  if (sentryPromise) return sentryPromise;
  sentryPromise = import('@sentry/react')
    .then((mod) => mod as unknown as SentryLike)
    .catch((err: unknown) => {
      // Deliberately a warning, not silence: "error reporting is off"
      // is a thing the founder needs to be able to discover.
      console.warn(
        '[observability] @sentry/react could not be loaded — error reporting is OFF. ' +
          'Run `npm install` (SCALE-2 added it to package.json but the lockfile ' +
          'was never updated). Detail:',
        err,
      );
      return null;
    });
  return sentryPromise;
}

/**
 * Run `fn` against the SDK if — and only if — it is available. Fire and
 * forget: returns immediately, never throws, never rejects. Every
 * exported capture/identity function goes through this so an SDK bug
 * can't become an Orbit bug.
 */
function withSentry(fn: (sentry: SentryLike) => void): void {
  if (!observabilityEnabled) return;
  try {
    void loadSentry().then((sentry) => {
      if (!sentry || !sdkReady) return;
      try {
        fn(sentry);
      } catch (err) {
        console.warn('[observability] Sentry call threw — ignored:', err);
      }
    });
  } catch (err) {
    console.warn('[observability] Sentry dispatch threw — ignored:', err);
  }
}

// ─────────────────────────────────────────────────────────────────────
// Scrubbing
// ─────────────────────────────────────────────────────────────────────

/**
 * Keys whose VALUE is redacted wholesale, wherever they appear —
 * event extra, contexts, breadcrumb data, request data, tags.
 *
 * Deliberately broad. A false positive costs a redacted debugging
 * field; a false negative puts a client's email or a signing token in
 * a third-party system we do not control.
 */
const SENSITIVE_KEY = new RegExp(
  [
    // credentials / secrets
    'token', 'secret', 'password', 'passwd', 'authorization', 'auth',
    'bearer', 'jwt', 'apikey', 'api_key', 'anon_key', 'service_role',
    'session', 'cookie', 'signature', 'dsn', 'credential',
    // Orbit's own capability strings — these ARE the credential for
    // the public surfaces (client portal, /d/:token sign page,
    // gallery access codes, booking links)
    'access_code', 'accesscode', 'public_token', 'share_token',
    'signing', 'otp', 'pin',
    // client PII
    'email', 'e_mail', 'phone', 'mobile', 'address', 'street',
    'postal', 'zip', 'full_name', 'fullname', 'first_name',
    'last_name', 'client_name', 'couple', 'signer',
    // payment
    'card', 'cvc', 'cvv', 'iban', 'account_number', 'routing',
    'ssn', 'tax_id',
  ].join('|'),
  'i',
);

/** Value-shaped detectors — catches secrets that arrive under an
 *  innocent key (`{ detail: "sk_live_…" }`). */
const EMAIL_RE = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;
const JWT_RE = /\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]+/g;
const STRIPE_RE = /\b(?:sk|rk|pk|whsec)_(?:live|test)?_?[A-Za-z0-9]{12,}/g;
const BEARER_RE = /\bBearer\s+[A-Za-z0-9._~+/=-]{12,}/gi;
const LONG_HEX_RE = /\b[a-f0-9]{32,}\b/gi;
/** 16+ digit runs — card numbers with or without separators. */
const CARDISH_RE = /\b(?:\d[ -]*?){13,19}\b/g;

const REDACTED = '[redacted]';

/** Redact secret-shaped substrings inside a free-text string. */
export function scrubText(input: string): string {
  return input
    .replace(EMAIL_RE, '[email]')
    .replace(JWT_RE, '[jwt]')
    .replace(STRIPE_RE, '[stripe-key]')
    .replace(BEARER_RE, 'Bearer [redacted]')
    .replace(CARDISH_RE, '[number]')
    .replace(LONG_HEX_RE, '[hex]');
}

/**
 * Strip a URL down to origin + path, with token-shaped path segments
 * masked. Orbit puts capability strings IN THE PATH — `/d/:token`,
 * `/portal/:token`, `/g/:code` — so dropping the query string alone
 * would still leak a working signing link.
 */
export function scrubUrl(raw: string): string {
  try {
    const u = new URL(raw, window.location.origin);
    const path = u.pathname
      .split('/')
      .map((seg) => {
        if (seg.length >= 12 && /^[A-Za-z0-9_-]+$/.test(seg)) return ':token';
        if (/^[0-9a-f-]{36}$/i.test(seg)) return ':uuid';
        return seg;
      })
      .join('/');
    // Query and hash dropped entirely — never worth the risk.
    return `${u.origin}${path}${u.search ? '?[stripped]' : ''}`;
  } catch {
    return scrubText(raw);
  }
}

/** Recursive scrub for anything structured we're about to send. */
function scrubValue(value: unknown, depth = 0): unknown {
  if (depth > 6) return '[depth]';
  if (value == null) return value;
  if (typeof value === 'string') {
    return scrubText(value.length > 2000 ? `${value.slice(0, 2000)}…` : value);
  }
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (Array.isArray(value)) {
    return value.slice(0, 50).map((v) => scrubValue(v, depth + 1));
  }
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = SENSITIVE_KEY.test(k) ? REDACTED : scrubValue(v, depth + 1);
    }
    return out;
  }
  return '[unserialisable]';
}

/** Header allowlist — everything else is dropped, not scrubbed. */
const SAFE_HEADERS = new Set(['content-type', 'accept', 'user-agent']);

// ─────────────────────────────────────────────────────────────────────
// Environment / release
// ─────────────────────────────────────────────────────────────────────

/**
 * `production` only on the real domain. Vercel preview deploys and
 * local dev report as `preview` / `development`, so a preview crash
 * can never page anyone or pollute the production issue stream.
 *
 * `__ORBIT_ENV__` comes from Vercel's `VERCEL_ENV` at build time
 * (`production` | `preview` | `development`). The hostname check is a
 * fallback for builds that ran outside Vercel.
 */
function resolveEnvironment(): string {
  const injected = typeof __ORBIT_ENV__ === 'string' ? __ORBIT_ENV__ : '';
  if (injected) return injected;
  if (import.meta.env.DEV) return 'development';
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  if (/vercel\.app$/i.test(host)) return 'preview';
  if (/^localhost$|^127\./.test(host)) return 'development';
  return 'production';
}

/** Commit sha of the deploy, so an issue maps to a build. Matches the
 *  `buildId` that `vite.config.ts`'s build-info plugin writes into
 *  `/build.json`, which is what `updateChecker.ts` already polls —
 *  same identifier in both places on purpose. */
function resolveRelease(): string {
  const injected = typeof __ORBIT_RELEASE__ === 'string' ? __ORBIT_RELEASE__ : '';
  return injected || 'dev';
}

// ─────────────────────────────────────────────────────────────────────
// Init
// ─────────────────────────────────────────────────────────────────────

/**
 * Call ONCE, as early as possible in `main.tsx` — before `createRoot`
 * so a crash during first render is still captured.
 *
 * HOTFIX-1 — SYNCHRONOUS AND NON-BLOCKING. It returns before the SDK
 * has loaded (or failed to load); the actual `Sentry.init` happens on a
 * microtask off the dynamic import. `createRoot` is therefore never
 * waiting on anything here. Nothing it does can throw into the caller.
 *
 * The cost of that: errors thrown in the first few milliseconds of the
 * very first render can be missed. That is the correct trade — a
 * reported crash is worth less than an app that boots.
 *
 * No-ops (loudly, once) when `VITE_SENTRY_DSN` is unset.
 */
export function initObservability(): void {
  if (initialised) return;
  initialised = true;

  if (!DSN) {
    // Deliberately a console line, not silence: "we shipped error
    // tracking" and "error tracking is running" are different claims
    // and the difference is one env var.
    console.info(
      '[observability] VITE_SENTRY_DSN is not set — error reporting is OFF. ' +
        'See docs/lovable-deploy-prompts.md (SCALE-2).',
    );
    return;
  }

  void loadSentry().then((sentry) => {
    if (!sentry) return; // already warned in loadSentry
    try {
      sentry.init(buildSentryOptions());
      sdkReady = true;
    } catch (err) {
      // A bad DSN, a blocked transport, an SDK version mismatch — all
      // land here. None of them are worth a broken app.
      console.warn(
        '[observability] Sentry.init failed — error reporting is OFF:',
        err,
      );
      sdkReady = false;
    }
  });
}

/** The init options, hoisted out so `initObservability` stays trivially
 *  readable and so the options can be unit-tested without an SDK. */
function buildSentryOptions(): Record<string, unknown> {
  return {
    dsn: DSN,
    environment: resolveEnvironment(),
    release: resolveRelease(),

    // PII: off at every level we control.
    sendDefaultPii: false,

    // Errors only by default. Tracing is a separate (large) quota and
    // Orbit has no latency budget to defend yet; opt in per-env with
    // VITE_SENTRY_TRACES_SAMPLE_RATE if that changes.
    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0),

    // NO Session Replay integration. See the header — it would record
    // client names, contract bodies and invoice amounts verbatim.
    integrations: (defaults: Array<{ name: string }>) =>
      defaults.filter((i) => {
        // Drop anything replay/profiling shaped even if a future SDK
        // version adds it to the defaults.
        if (/replay|profil/i.test(i.name)) return false;
        return true;
      }),

    // Breadcrumbs: keep navigation and fetch shapes, drop console
    // noise below `warn` (debug.log payloads carry row data).
    beforeBreadcrumb(crumb: {
      category?: string;
      level?: string;
      message?: unknown;
      data?: unknown;
    }) {
      if (crumb.category === 'console') {
        const level = crumb.level ?? 'log';
        if (level !== 'warn' && level !== 'error') return null;
      }
      if (crumb.data && typeof crumb.data === 'object') {
        crumb.data = scrubValue(crumb.data) as Record<string, unknown>;
      }
      if (typeof crumb.message === 'string') {
        crumb.message = scrubText(crumb.message);
      }
      // xhr/fetch crumbs carry the full URL including tokens.
      const url = (crumb.data as { url?: unknown } | undefined)?.url;
      if (typeof url === 'string') {
        (crumb.data as Record<string, unknown>).url = scrubUrl(url);
      }
      return crumb;
    },

    // Known-noise suppression. These are browser/extension artefacts,
    // not Orbit bugs, and at 10,000 studios they would eat the quota.
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      // User navigated away mid-request. Not actionable.
      'AbortError',
      'The operation was aborted',
      'NetworkError when attempting to fetch resource',
      'Failed to fetch',
      'Load failed',
    ],
    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^safari-(web-)?extension:\/\//i,
    ],

    beforeSend(event: SentryEventLike) {
      try {
        return scrubEvent(event);
      } catch {
        // If scrubbing itself throws we drop the event. Sending an
        // unscrubbed payload is the one failure mode this module
        // exists to prevent.
        return null;
      }
    },
  };
}

/** The single scrub pass applied to every outbound event. Exported so
 *  it can be unit-tested without booting the SDK. */
export function scrubEvent(event: SentryEventLike): SentryEventLike | null {
  // 1. Never send an email/name, even if something set one.
  if (event.user) {
    event.user = {
      id: typeof event.user.id === 'string' ? event.user.id : undefined,
      // Explicitly cleared. `sendDefaultPii: false` already stops the
      // SDK sending `{{auto}}`, but say it out loud so nobody
      // "helpfully" turns PII on later and quietly starts storing IPs.
      ip_address: undefined,
      email: undefined,
      username: undefined,
    };
  }

  // 2. URLs — page URL, referrer, and every frame's filename.
  if (event.request) {
    if (typeof event.request.url === 'string') {
      event.request.url = scrubUrl(event.request.url);
    }
    event.request.query_string = undefined;
    event.request.cookies = undefined;
    event.request.data = event.request.data
      ? (scrubValue(event.request.data) as typeof event.request.data)
      : undefined;
    if (event.request.headers) {
      const kept: Record<string, string> = {};
      for (const [k, v] of Object.entries(event.request.headers)) {
        if (SAFE_HEADERS.has(k.toLowerCase())) kept[k] = String(v);
      }
      event.request.headers = kept;
    }
  }

  // 3. Message + exception values.
  if (typeof event.message === 'string') {
    event.message = scrubText(event.message);
  }
  for (const ex of event.exception?.values ?? []) {
    if (typeof ex.value === 'string') ex.value = scrubText(ex.value);
  }

  // 4. Everything structured.
  if (event.extra) event.extra = scrubValue(event.extra) as typeof event.extra;
  if (event.contexts) {
    event.contexts = scrubValue(event.contexts) as typeof event.contexts;
  }
  if (event.tags) {
    const tags: Record<string, string> = {};
    for (const [k, v] of Object.entries(event.tags)) {
      tags[k] = SENSITIVE_KEY.test(k) ? REDACTED : scrubText(String(v));
    }
    event.tags = tags;
  }

  return event;
}

// ─────────────────────────────────────────────────────────────────────
// Identity
// ─────────────────────────────────────────────────────────────────────

/**
 * Attach WHO to every subsequent event — opaque uuids only.
 *
 * `userId` is the Supabase auth uuid and `orgId` the studio's org
 * uuid. Both are meaningless outside Orbit's own database, which is
 * exactly the point: they let the founder answer "is this one studio
 * or all of them" without putting a name or an email in Sentry.
 *
 * Do NOT extend this to email, studio name or plan name. Plan tier is
 * fine as a tag if it's ever needed — it isn't identifying.
 */
export function setObservabilityIdentity(
  userId: string | null | undefined,
  orgId: string | null | undefined,
): void {
  // HOTFIX-1 — fire-and-forget. `ObservabilityIdentity` calls this from
  // an effect INSIDE the auth tree; it must be impossible for it to
  // throw, suspend, or return a promise anyone might await.
  withSentry((sentry) => {
    if (!userId) {
      sentry.setUser(null);
      // Signed out — the tag has to be actively cleared, not left
      // pointing at the previous session's org.
      sentry.setTag('org_id', 'signed-out');
      return;
    }
    sentry.setUser({ id: userId });
    sentry.setTag('org_id', orgId ?? 'none');
  });
}

// ─────────────────────────────────────────────────────────────────────
// Capture
// ─────────────────────────────────────────────────────────────────────

export interface CaptureContext {
  /** Short, stable, greppable. e.g. `galleries.fetchPhotos`. */
  op: string;
  /** Extra breadcrumbs — scrubbed before send. Never put a row in here. */
  extra?: Record<string, unknown>;
  level?: 'fatal' | 'error' | 'warning' | 'info';
}

/**
 * Report a caught error. Use this at the ~105 `catch` blocks that
 * currently render failure as empty data — the point of SCALE-2 is
 * that a swallowed error becomes a reportable event, not that it
 * becomes a thrown one.
 *
 * Always also logs to the console, so behaviour is identical with and
 * without a DSN and local debugging doesn't depend on the vendor.
 */
export function captureAppError(error: unknown, ctx: CaptureContext): void {
  const level = ctx.level ?? 'error';
  // Console first — this is the part that works with no DSN.
  // HOTFIX-1 — wrapped, because `debug.error` calls this on paths that
  // run during boot, and a formatting throw here used to be able to
  // take the caller down with it.
  try {
    const line = `[${ctx.op}] ${error instanceof Error ? error.message : String(error)}`;
    if (level === 'warning' || level === 'info') console.warn(line, error);
    else console.error(line, error);
  } catch {
    console.error('[observability] failed to format an error for logging', error);
  }

  withSentry((sentry) => {
    sentry.withScope((scope) => {
      scope.setTag('op', ctx.op);
      scope.setLevel(level);
      if (ctx.extra) scope.setContext('detail', scrubValue(ctx.extra) as Record<string, unknown>);
      if (error instanceof Error) {
        sentry.captureException(error);
      } else {
        sentry.captureMessage(scrubText(String(error)), level);
      }
    });
  });
}

/** React error-boundary hand-off. Keeps the component stack, which is
 *  the whole reason the boundary is worth reporting from. */
export function captureBoundaryError(
  error: Error,
  componentStack: string | null | undefined,
  boundary: string,
): void {
  // HOTFIX-1 — the console line is unconditional now. Previously this
  // returned early with no DSN, so with reporting off a boundary catch
  // produced nothing from this function at all. `ErrorBoundary` logs its
  // own line too, but this one carries the boundary NAME, which is the
  // part that says which screen died.
  console.error(`[react.boundary.${boundary}]`, error);

  withSentry((sentry) => {
    sentry.withScope((scope) => {
      scope.setTag('op', `react.boundary.${boundary}`);
      scope.setLevel('fatal');
      if (componentStack) {
        scope.setContext('react', { componentStack: scrubText(componentStack) });
      }
      sentry.captureException(error);
    });
  });
}
